import type { Ref } from 'vue';
import { toRefs, watch } from 'vue';
import type { FieldHtmlNameType, HekateDisplayForm } from '@onshape/www-inertia';
import { FieldHtmlNameUtil } from '@onshape/www-inertia';

import ExternalUtil from '../../../../ExternalUtil';
import CommonPreFilledProgressiveProfile from './CommonPreFilledProgressiveProfileDefinition';

/**
 * Set the user's email preferences. Used cookie value set by a custom eloqua
 * landing page to populate data when the user clicks on a special link in an
 * email sent by Eloqua
 */
export default class EmailSubscriptionPreferencesProfileDefinition extends CommonPreFilledProgressiveProfile {
  private static readonly preferenceCookieName = 'emailVarForPreferenceCenter';

  /**
   * Copy over all the visible checkboxes to hidden text fields. This is to get over a limitation of Eloqua's Field merge
   */
  public override realtimeBranchModifications({
    formDefinition,
  }: {
    formDefinition: Ref<HekateDisplayForm>;
  }): void {
    super.realtimeBranchModifications({ formDefinition });

    EmailSubscriptionPreferencesProfileDefinition.handleEmail(formDefinition);
    EmailSubscriptionPreferencesProfileDefinition.mirrorCheckboxes(formDefinition);
    EmailSubscriptionPreferencesProfileDefinition.setValuesFromCookie(formDefinition);
  }

  /** email must only come from the cookie or this should be empty */
  private static handleEmail(formDefinition: Ref<HekateDisplayForm>) {
    // email must only come from the cookie or this should be empty
    const emailField = ExternalUtil.findDisplayFieldOrThrow({
      fieldName: FieldHtmlNameUtil.email,
      formDefinition,
    });
    emailField.isIgnorePrefillAndEventBusValues = false;
    emailField.val = '';
  }

  /** mirror checkboxes to hidden fields and handle the checking/un-checking for unsubscribe/pause */
  private static mirrorCheckboxes(formDefinition: Ref<HekateDisplayForm>) {
    // copy the `boolean` checkbox to the string hidden field on change
    const mirroredValues = [
      [FieldHtmlNameUtil.isUnsubscribe, FieldHtmlNameUtil.isHUnsubscribe],
      [
        FieldHtmlNameUtil.isPremiumContentSubscription,
        FieldHtmlNameUtil.isHPremiumContentSubscription,
      ], // this has been changed to productUpdates
      [
        FieldHtmlNameUtil.isUpcomingEventsSubscription,
        FieldHtmlNameUtil.isHUpcomingEventsSubscription,
      ],
      [
        FieldHtmlNameUtil.isOnshapeNewsletterSubscription,
        FieldHtmlNameUtil.isHOnshapeNewsletterSubscription,
      ],
      [FieldHtmlNameUtil.pauseEmailsFor30, FieldHtmlNameUtil.isHPauseEmailsFor30],
    ];

    // watch the unsubscribe checkbox to (un)check the other subscribe fields
    const unsubscribeField = ExternalUtil.findDisplayFieldOrThrow({
      fieldName: FieldHtmlNameUtil.isUnsubscribe,
      formDefinition,
    });

    const contentFields = mirroredValues
      .map((v) => v[0])
      .filter((fieldName) => fieldName !== FieldHtmlNameUtil.isUnsubscribe)
      .filter((fieldName) => fieldName !== FieldHtmlNameUtil.pauseEmailsFor30)
      .map((fieldName) =>
        ExternalUtil.findDisplayFieldOrThrow({
          fieldName: fieldName as FieldHtmlNameType,
          formDefinition,
        })
      );
    const contentVals = contentFields.map((f) => toRefs(f).val);
    const unsubscribeFieldVal = toRefs(unsubscribeField).val;

    // if no boxes are checked unsubscribe must be checked
    const preferredLanguage = ExternalUtil.findDisplayFieldOrThrow({
      fieldName: FieldHtmlNameUtil.preferredLanguage,
      formDefinition,
    });
    watch(contentVals, (newVal) => {
      const found = newVal.find((v) => v);
      unsubscribeField.val = !found;
    });

    const pauseEmailsFor30Checkbox = ExternalUtil.findDisplayFieldOrThrow({
      fieldName: FieldHtmlNameUtil.pauseEmailsFor30,
      formDefinition,
    });
    const pauseEmailsExpiryHidden = ExternalUtil.findDisplayFieldOrThrow({
      fieldName: FieldHtmlNameUtil.pauseEmailsExpiry,
      formDefinition,
    });
    // when the user clicks the unsubscribe un-click all the content checkboxes
    watch(unsubscribeFieldVal, (newVal) => {
      // if we are un-checking unsubscribe any content is unchecked this is a no-op
      if (!newVal && contentVals.find((v) => v.value)) {
        return;
      }
      preferredLanguage.isDisabled = false;

      if (newVal) {
        if (!preferredLanguage.val) {
          preferredLanguage.val = 'en';
        }
        preferredLanguage.errorMessages = [];
        pauseEmailsFor30Checkbox.val = false;
        pauseEmailsExpiryHidden.val = '';
        preferredLanguage.isDisabled = true;
      }

      contentFields.forEach((f) => {
        f.val = !newVal;
      });
    });

    const watchedFlags = mirroredValues
      .map((vv) => vv[0])
      .map((fieldName) =>
        ExternalUtil.findDisplayFieldOrThrow({
          fieldName: fieldName as FieldHtmlNameType,
          formDefinition,
        })
      );

    // copy the `boolean` checkbox to the string hidden field on change
    watch(watchedFlags, () => {
      mirroredValues.forEach(([fromName, toName]) => {
        const from = ExternalUtil.findDisplayFieldOrThrow({
          fieldName: fromName as FieldHtmlNameType,
          formDefinition,
        });

        ExternalUtil.findDisplayFieldOrThrow({
          fieldName: toName as FieldHtmlNameType,
          formDefinition,
        }).val = `${from.val}`;
      });
    });

    // pause email should uncheck set an hidden expiry. Also make sure that if
    // the user chooses pause that they are subscribed to at least one set of
    // emails (if they arne't just put them in all)
    const pauseEmailsFor30CheckboxVal = toRefs(pauseEmailsFor30Checkbox).val;
    watch(pauseEmailsFor30CheckboxVal, (newVal) => {
      if (!newVal) {
        pauseEmailsExpiryHidden.val = '';
        return;
      }
      // expires 30 days from now
      const expiry = new Date(Date.now() + 30 * 60 * 60 * 24 * 1000).toISOString().split('T')[0];
      pauseEmailsExpiryHidden.val = expiry as string;

      // if no content fields are checked, check them all
      if (contentFields.find((f) => f.val)) {
        return;
      }
      contentFields.forEach((f) => {
        f.val = true;
      });
    });
  }

  /** Prefill checkboxes based on the values stored in the preference cookie */
  private static setValuesFromCookie(formDefinition: Ref<HekateDisplayForm>) {
    // Get the values to set from either the defaults or the cookie
    const defaults = ExternalUtil.parsePreferenceCookie({
      cookieName: EmailSubscriptionPreferencesProfileDefinition.preferenceCookieName,
      defaults: {
        isPremiumContentSubscription: true, // this has been changed to productUpdates
        isUpcomingEventsSubscription: true,
        isOnshapeNewsletterSubscription: true,
        isUnsubscribe: false,
        preferredLanguage: 'en',
        email: '',
      },
    });

    // Field names to pre-populate either from the cookie or defaults
    const fieldNames: FieldHtmlNameType[] = [
      FieldHtmlNameUtil.isPremiumContentSubscription, // this has been changed to productUpdates
      FieldHtmlNameUtil.isUpcomingEventsSubscription,
      FieldHtmlNameUtil.isOnshapeNewsletterSubscription,
      FieldHtmlNameUtil.isUnsubscribe,
      FieldHtmlNameUtil.preferredLanguage,
      FieldHtmlNameUtil.email,
    ];

    ExternalUtil.populateDefaultFields({
      formDefinition,
      fieldNames,
      defaults,
    });
  }
}
